/**
 * complete GLB 的实例 ID 根 node 选择策略。
 *
 * OpenProject/GetCadNodeTree 返回的 CadNode.instance_id 与归档 GLB node.extras.instance_id
 * 属于同一稳定 occurrence 身份。策略只在活动 scene 的可达树中执行精确匹配，允许目标位于
 * 任意嵌套层级，但不按名称、node_id、数组顺序或不可达 definition 做兼容兜底。
 */
export class InstanceIdCompleteGlbRootNodeSelectionStrategy
  implements PuppyCAD.CompleteGlbRootNodeSelectionStrategy
{
  /** 当前目标 occurrence 实例 ID。 */
  private readonly instanceId: string

  /** 当前策略参与 parsed GLB cache 隔离的稳定版本键。 */
  public readonly cacheKey: string

  /**
   * 创建实例 ID 根选择策略。
   *
   * @param instanceId OpenProject/GetCadNodeTree 当前 CadNode.instance_id。
   */
  public constructor(instanceId: string) {
    const normalizedInstanceId = instanceId.trim()
    if (!normalizedInstanceId) {
      throw new Error('完整 GLB 根选择失败：CadNode.instance_id 不能为空')
    }
    this.instanceId = normalizedInstanceId
    this.cacheKey = `instance-id:v1:${encodeURIComponent(normalizedInstanceId)}`
  }

  /**
   * 按 instance_id 选择活动 scene 中唯一匹配的嵌套 node。
   *
   * @param json GLTFLoader 已解析、尚未实例化的原始 JSON。
   * @returns 原活动 scene 与唯一匹配的 node 索引。
   */
  public select(
    json: PuppyCAD.CompleteGlbJsonDocument,
  ): PuppyCAD.CompleteGlbRootNodeSelection {
    const scenes = json.scenes
    if (!Array.isArray(scenes) || scenes.length === 0) {
      throw new Error('完整 GLB 根选择失败：缺少 scenes 定义')
    }

    const sceneIndex = json.scene ?? 0
    if (!Number.isInteger(sceneIndex) || sceneIndex < 0 || sceneIndex >= scenes.length) {
      throw new Error(`完整 GLB 根选择失败：活动 scene 索引非法，sceneIndex=${sceneIndex}`)
    }
    const nodes = json.nodes
    if (!Array.isArray(nodes)) {
      throw new Error('完整 GLB 根选择失败：缺少 nodes 定义')
    }

    const pendingNodeIndices = [...(scenes[sceneIndex]?.nodes ?? [])]
    const visitedNodeIndices = new Set<number>()
    const matchedNodeIndices: number[] = []
    while (pendingNodeIndices.length > 0) {
      const nodeIndex = pendingNodeIndices.pop()
      if (nodeIndex === undefined || visitedNodeIndices.has(nodeIndex)) {
        continue
      }
      if (!Number.isInteger(nodeIndex) || nodeIndex < 0 || nodeIndex >= nodes.length) {
        throw new Error(
          `完整 GLB 根选择失败：活动 scene 引用非法 node，sceneIndex=${sceneIndex}, nodeIndex=${String(nodeIndex)}`,
        )
      }

      const node = nodes[nodeIndex]
      if (!node) {
        throw new Error(
          `完整 GLB 根选择失败：活动 scene 引用不存在的 node，sceneIndex=${sceneIndex}, nodeIndex=${nodeIndex}`,
        )
      }
      visitedNodeIndices.add(nodeIndex)
      if (node.extras?.instance_id?.trim() === this.instanceId) {
        matchedNodeIndices.push(nodeIndex)
      }
      pendingNodeIndices.push(...(node.children ?? []))
    }

    if (matchedNodeIndices.length === 0) {
      throw new Error(
        `完整 GLB 根选择失败：instance_id 未命中，instanceId=${this.instanceId}, sceneIndex=${sceneIndex}`,
      )
    }
    if (matchedNodeIndices.length > 1) {
      throw new Error(
        `完整 GLB 根选择失败：instance_id 重复，instanceId=${this.instanceId}, nodeIndices=${matchedNodeIndices.join(',')}`,
      )
    }

    return {
      sceneIndex,
      nodeIndex: matchedNodeIndices[0]!,
    }
  }
}
